import { SPAIN_TIME_ZONE } from './constants'
import { extractDateParts } from './resolveEventDateCase'
import type { DateParts } from './resolveEventDateCase'

/** Formateador cacheado a nivel de módulo: construir un `Intl.DateTimeFormat`
 * es caro y este se evalúa en cada render de ficha. */
const SPAIN_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: SPAIN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Clave ordenable `YYYY-MM-DD`: al ser de ancho fijo y con ceros a la
 * izquierda, su orden lexicográfico coincide con el cronológico — misma razón
 * por la que `resolveEventDateCase` compara cadenas y nunca construye un
 * `Date`. */
function toSortableKey(parts: DateParts): string {
  return `${parts.year}-${parts.month}-${parts.day}`
}

/**
 * «Hoy» como día de calendario español.
 *
 * Se leen las PARTES (`formatToParts`) y no la cadena ya formateada: así el
 * resultado depende solo de la zona horaria y no del patrón de la locale.
 */
function resolveTodayInSpain(now: Date): DateParts {
  const parts = SPAIN_DATE_FORMATTER.formatToParts(now)
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''
  return { year: read('year'), month: read('month'), day: read('day') }
}

export interface IsEventPastInput {
  /** `start_at` del evento. */
  readonly start: string | null
  /** `end_at` del evento; manda sobre `start` cuando es una fecha usable. */
  readonly end: string | null
  /** Texto de fecha editorial que sustituye a las fechas: con contenido, la
   * fecha del evento es desconocida y NUNCA se considera pasada. */
  readonly dateNotVisible?: string | null
  /** Inyectable para test — mismo patrón que `resolvePurchaseState`. */
  readonly now?: Date
}

/**
 * ¿La fecha de celebración del evento ya pasó?
 *
 * Comparación a granularidad de **día**, no de hora, y estricta: el día del
 * evento todavía no es pasado. Ambos lados de la comparación son cadenas
 * `YYYY-MM-DD`; del evento se toma el prefijo de fecha tal cual llega (sin
 * construir un `Date`, que desplazaría el día en fechas-hora cercanas a
 * medianoche), y el «hoy» se resuelve en la zona horaria del negocio. El efecto
 * buscado es que este predicado cambie el mismo día que la ficha *muestra* como
 * fecha del evento.
 *
 * No confundir con `isSaleEnded` (`lib/purchaseState`), que mira la ventana de
 * VENTA de entradas y sí compara instantes absolutos.
 */
export function isEventPast(input: IsEventPastInput): boolean {
  if (input.dateNotVisible?.trim()) return false

  // `end` manda: un evento de varios días sigue vigente hasta que pasa el
  // último. Se resuelve DESPUÉS de extraer las partes (no con `end ?? start`
  // sobre la cadena cruda) para que un `end` presente pero no parseable caiga
  // en `start` en vez de anular la comprobación.
  const reference = extractDateParts(input.end) ?? extractDateParts(input.start)
  if (!reference) return false

  const today = resolveTodayInSpain(input.now ?? new Date())
  return toSortableKey(reference) < toSortableKey(today)
}
